import { cn } from "../../ui/utils";
import { formatValue } from "./chartTheme";
import type { NormalizedSeries } from "./resolveChart";

interface TooltipPayloadItem {
  name?: string | number;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
  payload?: Record<string, unknown> & { fill?: string };
}
interface InjectedTooltip {
  active?: boolean;
  label?: string | number;
  payload?: TooltipPayloadItem[];
}

const tooltipShell =
  "pointer-events-none max-w-[15rem] rounded-lg border border-border bg-popover px-2.5 py-2 text-[0.6875rem] shadow-xl";

export function CartesianTooltip({
  active,
  label,
  payload,
  unit = "",
  singleSeries = false,
  total = 0,
}: InjectedTooltip & { unit?: string; singleSeries?: boolean; total?: number }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={tooltipShell}>
      {label != null && String(label) !== "" && (
        <p className="mb-1.5 font-semibold text-foreground">{String(label)}</p>
      )}
      <div className="space-y-1">
        {payload.map((p, i) => {
          const color = p.color ?? p.payload?.fill;
          const value = Number(p.value);
          const share = singleSeries && total > 0 ? Math.round((value / total) * 100) : null;
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="size-2 shrink-0 rounded-[3px]" style={{ background: color }} />
              {!singleSeries && (
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{p.name}</span>
              )}
              <span className={cn("font-semibold tabular-nums text-foreground", !singleSeries && "ml-auto")}>
                {formatValue(value, unit)}
                {share != null && (
                  <span className="ml-1 font-normal text-muted-foreground">({share}%)</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RadialTooltip({
  active,
  payload,
  unit = "",
  total = 0,
}: InjectedTooltip & { unit?: string; total?: number }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const value = Number(item.value);
  const color = item.payload?.fill ?? item.color;
  const share = total > 0 ? Math.round((value / total) * 100) : null;
  return (
    <div className={tooltipShell}>
      <div className="flex items-center gap-2">
        <span className="size-2 shrink-0 rounded-[3px]" style={{ background: color }} />
        <span className="min-w-0 flex-1 truncate font-semibold text-foreground">{String(item.name ?? "")}</span>
        <span className="ml-auto font-semibold tabular-nums text-foreground">
          {formatValue(value, unit)}
          {share != null && <span className="ml-1 font-normal text-muted-foreground">({share}%)</span>}
        </span>
      </div>
    </div>
  );
}

export function CartesianLegend({ series }: { series: NormalizedSeries[] }) {
  return (
    <ul className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      {series.map((s) => (
        <li key={s.key} className="flex items-center gap-1.5 text-[0.6875rem] font-medium text-muted-foreground">
          <span className="size-2 shrink-0 rounded-[3px]" style={{ background: s.color }} />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

export interface RadialLegendItem {
  label: string;
  value: number;
  color: string;
  share: number;
}

export function RadialLegend({
  items,
  unit = "",
  active,
  onActivate,
}: {
  items: RadialLegendItem[];
  unit?: string;
  active: number | null;
  onActivate: (index: number | null) => void;
}) {
  return (
    <ul className="min-w-0 flex-1 space-y-0.5 self-stretch">
      {items.map((it, i) => {
        const on = active === i;
        return (
          <li
            key={i}
            onMouseEnter={() => onActivate(i)}
            onMouseLeave={() => onActivate(null)}
            className={cn(
              "flex cursor-default items-center gap-2 rounded-md px-1.5 py-1 text-xs leading-snug transition-colors",
              on && "bg-foreground/[0.06]",
            )}
          >
            <span className="size-2.5 shrink-0 rounded-sm" style={{ background: it.color }} />
            <span className={cn("min-w-0 flex-1 break-words text-foreground", on && "font-semibold")}>
              {it.label}
            </span>
            <span className="shrink-0 font-semibold tabular-nums text-foreground">
              {formatValue(it.value, unit)}
            </span>
            <span className="w-9 shrink-0 text-right tabular-nums text-muted-foreground">{it.share}%</span>
          </li>
        );
      })}
    </ul>
  );
}
